/**
 * @fileoverview Tarjeta de detalle de error para mensajes de WhatsApp no entregados.
 * @module MessageErrorDetailCardMC
 * @category Component - Molecule
 */

import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import IconMC from '../../../components/IconMC';

const SEVERITY_STYLES = {
  critical: {
    icon: 'alert-circle-fill',
    iconColor: 'tw-text-red-600',
    titleColor: 'tw-text-red-600',
    badge: 'tw-bg-red-100 tw-text-red-700',
    borderColor: 'tw-border-l-red-700',
    labelKey: 'errorDetailCard.severity_critical'
  },
  warning: {
    icon: 'alert-circle-fill',
    iconColor: 'tw-text-amber-600',
    titleColor: 'tw-text-amber-600',
    badge: 'tw-bg-amber-100 tw-text-amber-700',
    borderColor: 'tw-border-l-amber-700',
    labelKey: 'errorDetailCard.severity_warning'
  },
  info: {
    icon: 'info-fill',
    iconColor: 'tw-text-gray-500',
    titleColor: 'tw-text-gray-700',
    badge: 'tw-bg-blue-100 tw-text-blue-700',
    borderColor: 'tw-border-l-blue-700',
    labelKey: 'errorDetailCard.severity_info'
  }
};

const MessageErrorDetailCardMC = memo(({ errorDetails }) => {
  const { t } = useTranslation('InboxMC');

  const severityStyle = useMemo(
    () => SEVERITY_STYLES[errorDetails?.severity] || SEVERITY_STYLES.warning,
    [errorDetails?.severity]
  );

  const actions = useMemo(() => errorDetails?.actions || [], [errorDetails?.actions]);

  if (!errorDetails) return null;

  return (
    <div className="tw-wrapper tw-flex tw-w-full tw-my-1 tw-justify-end">
      <div className={`tw-max-w-[85%] tw-w-full tw-min-w-0 tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-shadow tw-shadow-gray-200 tw-p-4 tw-text-sm tw-border-l-[5px] ${severityStyle.borderColor}`}>
        <div className="tw-flex tw-items-start">
          <IconMC name={severityStyle.icon} size="lg" className={`${severityStyle.iconColor} tw-flex-shrink-0 tw-mt-0.5 tw-mr-2`} />
          <div className="tw-flex-1 tw-min-w-0">
            <p className={`tw-font-bold tw-m-0 tw-break-words ${severityStyle.titleColor}`}>{errorDetails.title}</p>
            <p className="tw-text-gray-700 tw-mt-1 tw-mb-0 tw-break-words">{errorDetails.description}</p>
          </div>
        </div>
        {actions.length > 0 && (
          <div className="tw-mt-3 tw-pt-3 tw-border-t tw-border-gray-100">
            <p className="tw-font-semibold tw-text-gray-800 tw-m-0">{t('errorDetailCard.what_to_do')}</p>
            <ol className="tw-list-decimal tw-pl-5 tw-mt-1 tw-mb-0 tw-text-gray-700">
              {actions.map((action) => (
                <li key={action} className="tw-mb-1 tw-break-words">{action}</li>
              ))}
            </ol>
          </div>
        )}
        <div className="tw-flex tw-items-center tw-justify-between tw-mt-3 tw-pt-2 tw-border-t tw-border-gray-100">
          <span className="tw-text-xs tw-text-gray-400">
            {errorDetails.code && `#${errorDetails.code}`}
            {errorDetails.code && errorDetails.source && ' · '}
            {errorDetails.source}
          </span>
          <span className={`tw-inline-block tw-py-1 tw-px-2 tw-rounded-md tw-text-xs tw-font-regular ${severityStyle.badge}`}>
            {t(severityStyle.labelKey)}
          </span>
        </div>
      </div>
    </div>
  );
});

MessageErrorDetailCardMC.displayName = 'MessageErrorDetailCardMC';

export default MessageErrorDetailCardMC;
